/**
 * Shared formatting utilities for consistent currency and number display
 * Ensures proper locale formatting for print and screen
 */

/**
 * Format a number as GBP currency with no decimal places
 * Uses en-GB locale for consistent formatting across environments
 * Handles edge cases like NaN, Infinity, null, and undefined
 */
export const gbp = (n: number | null | undefined): string => {
  // Handle invalid inputs
  if (n == null || !isFinite(n)) {
    return '—';  // En dash for missing/invalid data
  }
  
  return n.toLocaleString('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  });
};

/**
 * Format a number as GBP currency, or show a fallback message for missing data
 * Use this for baseline costs where we want to show "Not enough info yet"
 * Note: £0 is a valid amount and will be formatted normally
 */
export const gbpOrMessage = (n: number | null | undefined, fallbackMessage: string = 'Not enough info yet'): string => {
  if (n == null || !isFinite(n)) {
    return fallbackMessage;
  }
  return gbp(n);
};

/**
 * Format a number as kilograms with en-GB thousands separators
 * Handles edge cases like NaN, Infinity, null, and undefined
 */
export const kg = (n: number): string => {
  if (n == null || !isFinite(n)) {
    return '0 kg';
  }
  return `${Math.round(n).toLocaleString('en-GB')} kg`;
};

/**
 * Format a number with en-GB thousands separators
 * Handles edge cases like NaN, Infinity, null, and undefined
 */
export const formatNumber = (n: number): string => {
  if (n == null || !isFinite(n)) {
    return '0';
  }
  return Math.round(n).toLocaleString('en-GB');
};
